/**
 * Todo Real-Time Stream Composable
 *
 * Subscribes to todo:changed via Socket.IO and provides
 * a reactive badge count that updates instantly on todo mutations.
 * Replaces 60-second polling in SideNav.
 */

import { ref, watch, onUnmounted } from 'vue'
import { useSocket } from './useSocket'
import { apiGet } from '@/stores/api'
import logger from '@/utils/logger'

interface TodoEventPayload {
  event: string
  clientId: string | null
  data: { action: string; todoId: number; title?: string }
  timestamp: string
  source: string
  _subject: string
}

type TodoEventCallback = (_data: TodoEventPayload['data'], _payload: TodoEventPayload) => void

export function useTodoStream() {
  const { on, off, isConnected } = useSocket()
  const badgeCount = ref(0)
  const listeners = ref<Set<TodoEventCallback>>(new Set())

  // Fetch badge count from REST API
  async function refreshBadgeCount() {
    try {
      const response = await apiGet<{ data: { count: number } }>('/staff-todos/badge-count')
      badgeCount.value = response.data?.count || 0
    } catch {
      // ignore
    }
  }

  function handleTodoEvent(payload: TodoEventPayload) {
    // Refresh badge count on any todo change
    refreshBadgeCount()

    listeners.value.forEach(cb => {
      try {
        cb(payload.data, payload)
      } catch (err) {
        logger.error('[useTodoStream] Callback error:', err)
      }
    })
  }

  on('todo:changed', handleTodoEvent)

  onUnmounted(() => {
    off('todo:changed', handleTodoEvent)
    listeners.value.clear()
    if (fallbackTimer) {
      clearInterval(fallbackTimer)
      fallbackTimer = null
    }
  })

  // Polling fallback when disconnected
  let fallbackTimer: ReturnType<typeof setInterval> | null = null

  watch(isConnected, (connected) => {
    if (!connected) {
      if (!fallbackTimer) {
        fallbackTimer = setInterval(refreshBadgeCount, 60000)
      }
    } else {
      if (fallbackTimer) {
        clearInterval(fallbackTimer)
        fallbackTimer = null
      }
      // Catch up on reconnect
      refreshBadgeCount()
    }
  }, { immediate: true })

  function onTodoChanged(callback: TodoEventCallback) {
    listeners.value.add(callback)
    return () => listeners.value.delete(callback)
  }

  // Initial fetch
  refreshBadgeCount()

  return {
    badgeCount,
    onTodoChanged,
    refreshBadgeCount,
    isConnected,
  }
}
